/* eslint-disable react-hooks/exhaustive-deps */
import AuthService from "Lib/API/Auth";
import { useFormik } from "formik";
import { checkIsStripe, checkIsTranzila } from "helper/checkIsStripe";
import { setLocalizationMoment } from "helper/dateFormat";
import { SubHeader } from "layout/SubHeader";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Route, Switch, useHistory, useLocation } from "react-router-dom";
import FinanceService from "../../Lib/API/Finance";
import DonationCause from "./DonationCause/DonationCause";
import { getAccessTokenSilently, getAuthenticated } from "utils/functions";
import DonationDetails from "./DonationDetails/DonationDetails";
import DonationPay from "./DonationPay/DonationPay";
import PageNotFound from "./PageNotFound";
import "./style.scss";

const ExternalLinkDonation = ({ external }) => {
  const isAuthenticated = getAuthenticated();
  const { search, pathname } = useLocation();
  const searchParams = new URLSearchParams(search);
  const ledgerIdParams = searchParams.get("ledger");
  const subLedgerIdParams = searchParams.get("subledger");
  const logoParams = searchParams.get("logo");
  const { push } = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const [ledgers, setLedgers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enableCoverConvenienceFee, setEnableCoverConvenienceFee] = useState(
    false
  );
  const [enableBillMyAccount, setEnableBillMyAccount] = useState(false);

  const [paymentMethodDonation, setPaymentMethodDonation] = useState({});
  const [achPaymentMethodDonation, setAchPaymentMethodDonation] = useState({});
  const [selectedAchCard, setSelectedAchCard] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);

  const memberPortal = localStorage.getItem("memberPortal") === "true" || false;

  const enableCreditCardPay = paymentMethodDonation?.allow_member || external;
  const enableAchPay = useMemo(
    () =>
      achPaymentMethodDonation?.allow_member &&
      checkIsStripe(achPaymentMethodDonation),
    [achPaymentMethodDonation]
  );
  const disabledAchPay = enableAchPay && external;
  const displayAchTooltip = useMemo(
    () => (enableAchPay && external ? "Please login to pay with ACH" : ""),
    [enableAchPay, external]
  );

  const initialActivePayment = useMemo(() => {
    if (!external) {
      if (enableCreditCardPay) return "credit-card";
      if (enableAchPay) return "ach";
      if (enableBillMyAccount) return "bill-my-account";
      return "";
    }
    return "credit-card";
  }, [external, enableCreditCardPay, enableAchPay, enableBillMyAccount]);

  const formik = useFormik({
    isInitialValid: false,
    enableReinitialize: true,
    initialValues: {
      resultPage: "",
      showAcknowledgement: false,
      acknowledgements: [
        {
          recipient_name: "",
          email: "",
        },
      ],
      sendPaper: false,
      address: {
        location: {
          formattedAddress: "",
          place_id: "",
        },
        formattedAddress: "",
        state: "",
        city: "",
        zip_code: "",
        addressLine2: "",
        country: "",
      },
      activePayment: initialActivePayment,
      causeInfo: [
        {
          cause: "",
          amount: null,
          note: "",
          required_note: false,
          contribution: { isRecurring: false, frequency: "monthly" },
        },
      ],
    },
    validationSchema: "",
    onSubmit: async (values) => {},
  });

  useEffect(() => {
    const basePath = external ? "/donation" : "/donation-view";
    const isSubRoute = pathname !== basePath && pathname !== basePath + "/";
    const hasCause = formik.values?.causeInfo?.[0]?.cause;
    if (isSubRoute && !hasCause) {
      push(basePath);
    }
  }, []);

  useEffect(() => {
    formik.setFieldValue("resultPage", "");
    localStorage.setItem("DonationLayout", true);
    document.body.style.backgroundColor = "#fff";
    document.body.style.overflowX = "hidden";
    localStorage.removeItem("redirectUrl");
    if (external) {
      const generateTokenAndGetLedgers = async () => {
        const subDomain = window.location.hostname.split(".")[0];
        const hostName = subDomain === "localhost" ? (process.env.REACT_APP_LOCAL_ORGKEY || "stagerms") : subDomain;
        setLoading(true);
        try {
          const res = await AuthService.generateToken(hostName);
          if (!res.hasPayment) {
            window.location.replace(res?.shul_website);
            return;
          }
          setEnableBillMyAccount(res?.allowBillMyAccountOnDonation || false);
          setPaymentMethodDonation(res?.paymentMethodCreditCard || {});
          setAchPaymentMethodDonation(res?.paymentMethodAch || {});
          localStorage.setItem("externalToken", res?.token);
          localStorage.setItem("orgId", res?._id);
          localStorage.setItem("shul_website", res?.shul_website);
          localStorage.setItem("orgHasPayment", res?.hasPayment);
          localStorage.setItem("logoImg", res?.logoImg);
          localStorage.setItem("orgName", res?.orgName);
          document.title = `${res?.orgName}`;
          localStorage.setItem("country", JSON.stringify(res?.country || {}));
          localStorage.setItem("memberPortal", res?.memberPortal || false);

          setLocalizationMoment(res?.country?.local || "en");
          const led = await FinanceService.getLedgersForDonations(
            res?.token,
            res?._id
          );
          const LedgerWithSubLedger = [];

          if (led?.ledgers) {
            const ledAndSub = led?.ledgers?.map((item) => {
              if (item?.donation) {
                LedgerWithSubLedger.push(item);
              }
              const subLedger = item?.sub_ledgers?.map((sub) => {
                LedgerWithSubLedger.push({
                  name: item?.ledger_name,
                  ledger_id: item?._id,
                  ledger_account_receivable: item?.account_receivable,
                  sub_ledger_name: sub?.sub_ledger_name,
                  sub_ledger_id: sub?._id,
                  sub_ledger_amount_detuctible: sub?.amount_detuctible,
                  default_amount: sub?.default_amount,
                  sub_account_receivable: sub?.account_receivable,
                });
              });
            });
            setLedgers(LedgerWithSubLedger);
          }
        } catch (error) {
          enqueueSnackbar(error?.data?.message, {
            variant: "error",
          });
        } finally {
          setLoading(false);
        }
      };
      generateTokenAndGetLedgers();
    } else {
      if (localStorage.getItem("orgHasPayment") === "false") {
        push("/family-member-table");
      }

      const getLedgeraccounts = async () => {
        const token = await getAccessTokenSilently();
        setLoading(true);
        await FinanceService.getLedgersForDonations(token)
          .then((res) => {
            const LedgerWithSubLedger = [];

            if (res?.ledgers) {
              const ledAndSub = res?.ledgers?.map((item) => {
                if (item?.donation) {
                  LedgerWithSubLedger.push(item);
                }
                const subLedger = item?.sub_ledgers?.map((sub) => {
                  LedgerWithSubLedger.push({
                    name: item?.ledger_name,
                    ledger_id: item?._id,
                    ledger_account_receivable: item?.account_receivable,
                    sub_ledger_name: sub?.sub_ledger_name,
                    sub_ledger_id: sub?._id,
                    sub_ledger_amount_detuctible: sub?.amount_detuctible,
                    default_amount: sub?.default_amount,
                    sub_account_receivable: sub?.account_receivable,
                  });
                });
              });
              setLedgers(LedgerWithSubLedger);
            }
          })
          .catch((err) => {
            enqueueSnackbar(err?.data?.message, {
              variant: "error",
            });
          });
        setLoading(false);
      };
      getLedgeraccounts();
    }
    if (localStorage.getItem("donaitonFormik")) {
      formik.setValues(JSON.parse(localStorage.getItem("donaitonFormik")));
      localStorage.removeItem("donaitonFormik");
    }
    // Auto-populate details for authenticated users, even on the external donation URL
    const isLoggedInMember = !!localStorage.getItem("accountID") && !!localStorage.getItem("token");
    if (!external || isLoggedInMember) {
      if (localStorage.getItem("accountInfo")) {
        formik.setFieldValue(
          "first_name",
          JSON.parse(localStorage.getItem("accountInfo"))?.firstName
        );
        formik.setFieldValue(
          "last_name",
          JSON.parse(localStorage.getItem("accountInfo"))?.lastName
        );
        formik.setFieldValue(
          "receipt_email",
          JSON.parse(localStorage.getItem("accountInfo"))?.primary_email_address
        );
      }

      if (localStorage.getItem("allowBillMyAccountOnDonation")) {
        const allowBillMyAccountOnDonation = JSON.parse(
          localStorage.getItem("allowBillMyAccountOnDonation")
        );
        setEnableBillMyAccount(allowBillMyAccountOnDonation || false);
      }
    }
    if (localStorage.getItem("paymentMethodCreditCard")) {
      const paymentMethod = JSON.parse(
        localStorage.getItem("paymentMethodCreditCard")
      );
      setPaymentMethodDonation(paymentMethod);
    }

    if (localStorage.getItem("paymentMethodAch")) {
      const achPaymentMethod = JSON.parse(
        localStorage.getItem("paymentMethodAch")
      );
      setAchPaymentMethodDonation(achPaymentMethod);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    localStorage.removeItem("redirectUrl");
  }, []);

  useEffect(() => {
    if (formik.values?.activePayment === "credit-card") {
      return setEnableCoverConvenienceFee(
        paymentMethodDonation?.coverConvenienceFee
      );
    }
    if (formik.values?.activePayment === "ach") {
      return setEnableCoverConvenienceFee(
        achPaymentMethodDonation?.coverConvenienceFee
      );
    }
  }, [
    formik.values?.activePayment,
    paymentMethodDonation,
    achPaymentMethodDonation,
  ]);

  useEffect(() => {
    if (!ledgers?.length || (!subLedgerIdParams && !ledgerIdParams)) return;
    const selectedLedgerSubLedger = ledgers.find(
      (ledger) =>
        ledger?._id === ledgerIdParams ||
        ledger?.sub_ledger_id === subLedgerIdParams
    );
    if (!selectedLedgerSubLedger) {
      push({
        pathname,
        search: "",
      });
      return;
    }
    if (selectedLedgerSubLedger) {
      formik.setFieldValue("causeInfo", [
        {
          cause: selectedLedgerSubLedger,
          amount: selectedLedgerSubLedger?.default_amount ?? "",
          required_note: selectedLedgerSubLedger?.notes ?? "",
          contribution: { isRecurring: false, frequency: "monthly" },
        },
      ]);
    }
  }, [ledgers, ledgerIdParams, subLedgerIdParams]);

  const handleReserveLogin = () => {
    if (isAuthenticated) {
      localStorage.clear();
      window.location.reload();
      return;
    }
    localStorage.setItem("redirectUrl", `/donation-view`);
    window.location.href = "/login";
  };

  const getCurrentPaymentMethod = useCallback(() => {
    if (formik?.values?.activePayment === "credit-card") {
      return paymentMethodDonation;
    }
    if (formik?.values?.activePayment === "ach") {
      return achPaymentMethodDonation;
    }
    return null;
  }, [
    formik?.values?.activePayment,
    paymentMethodDonation,
    achPaymentMethodDonation,
  ]);

  return (
    <div
      className="p-5"
      style={{
        width: "100%",
      }}
    >
      {external && localStorage.getItem("logoImg") && logoParams !== "false" && (
        <div className="borderBottom  pb-5 donation__header d-flex justify-content-between align-items-center">
          <div>
            <img
              src={localStorage.getItem("logoImg")}
              alt="logo"
              style={{
                height: "50px",
                width: "130px",
                objectFit: "fill",
              }}
            />
          </div>
          {memberPortal && (
            <div>
              <button className="donation-login" onClick={handleReserveLogin}>
                {isAuthenticated ? "Logout" : "Login"}
              </button>
            </div>
          )}
        </div>
      )}
      {!external && null}
      <div className={`container_border ${!external ? "pt-10 pb-10" : ""}`}>
        {external && <div className="makeDonation">Make a Donation</div>}
        <div>
          <Switch>
            <Route exact path={external ? "/donation" : "/donation-view"}>
              <DonationCause
                formik={formik}
                ledgers={ledgers}
                loading={loading}
                external={external}
                hasLedgerParams={ledgerIdParams || subLedgerIdParams}
              />
            </Route>

            <Route
              exact
              path={external ? "/donation/details" : "/donation-view/details"}
            >
              <DonationDetails formik={formik} external={external} />
            </Route>

            <Route
              exact
              path={external ? "/donation/pay" : "/donation-view/pay"}
            >
              <DonationPay
                formik={formik}
                enableCoverConvenienceFee={enableCoverConvenienceFee}
                enableBillMyAccount={enableBillMyAccount}
                setEnableCoverConvenienceFee={setEnableCoverConvenienceFee}
                external={external}
                enableCreditCardPay={enableCreditCardPay}
                enableAchPay={enableAchPay}
                disabledAchPay={disabledAchPay}
                displayAchTooltip={displayAchTooltip}
                selectedAchCard={selectedAchCard}
                setSelectedAchCard={setSelectedAchCard}
                selectedCard={selectedCard}
                setSelectedCard={setSelectedCard}
                getCurrentPaymentMethod={getCurrentPaymentMethod}
                paymentMethodDonation={paymentMethodDonation}
                achPaymentMethodDonation={achPaymentMethodDonation}
              />
            </Route>
            <Route path="*">
              <PageNotFound />
            </Route>
          </Switch>
        </div>
      </div>
    </div>
  );
};

export default ExternalLinkDonation;
